import * as React from "react";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkErrorSummary } from "uitk-react-error-summary";
import { UitkLayoutGridItem, UitkLayoutConditionalGridSpan } from "uitk-react-layout-grid";

export interface LobErrorSummaryProps {
  /**
   * Localization token for the heading
   */
  locHeadingToken: string;
  /**
   * Localization arguments for the heading
   */
  locHeadingArgs?: any;
  /**
   * Spaces in the grid for the component
   */
  colSpan?: UitkLayoutConditionalGridSpan;
  /**
   * Localization token for description
   */
  locDescriptionToken?: string;
  /**
   * css classes for the component
   */
  className?: string;
  /**
   * Localization token for link
   */
  locLinkToken?: string;
  /**
   * Link's Id for focus
   */
  linkClickFocusId?: string;
  /**
   * Reference for the link
   */
  inputErrorRef?: React.RefObject<HTMLHeadingElement> | null;
  setInputsRefs?: (ref: React.RefObject<HTMLHeadingElement>) => void;
}

/**
 * Component for showing error summary when needed.
 *
 * @component
 * @example
 * const headingToken = "example.heading.token"
 * const descriptionToken = 'example.description.token'
 * const UitkLayoutConditionalGridSpan = { small: 1 as UitkLayoutGridColumnRow, medium: 6 as UitkLayoutGridColumnRow };
 * return (
 *   <LobErrorSummary
 *    locHeadingToken={headingToken}
 *    locDescriptionToken={descriptionToken}
 *    colSpan={UitkLayoutConditionalGridSpan}
 *   />
 * )
 */

export const LobErrorSummary = (props: LobErrorSummaryProps) => {
  const { formatText } = useLocalization();
  const {
    locHeadingToken,
    locDescriptionToken,
    colSpan,
    className,
    locHeadingArgs,
    locLinkToken,
    inputErrorRef,
    linkClickFocusId,
    setInputsRefs,
  } = props;
  const [focusElement] = React.useState(React.createRef<HTMLHeadingElement>());

  const handleUpdate = () => {
    if (setInputsRefs) {
      setInputsRefs(focusElement);
    }
    focusElement?.current?.focus();
  };

  const heading = locHeadingArgs ? formatText(locHeadingToken, ...locHeadingArgs) : formatText(locHeadingToken, "");

  return (
    <UitkLayoutGridItem colSpan={colSpan} className={className}>
      <div data-testid="lob-error-summary">
        <UitkErrorSummary
          heading={heading}
          description={locDescriptionToken && formatText(locDescriptionToken)}
          domRef={focusElement}
          handleUpdate={handleUpdate}
          link={locLinkToken && formatText(locLinkToken)}
          linkClickFocus={inputErrorRef}
          linkClickFocusId={linkClickFocusId}
        />
      </div>
    </UitkLayoutGridItem>
  );
};
